import React, { useState } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { simplifiedPosterHoverEnabled } from 'helpers/ConfigHelper';
import { isSeries, isTypeSeries } from 'helpers/ContentHelper';
import { getDynamicImage, POSTER_OVERLAY_IMAGE } from 'helpers/ImageHelper';
import { helperGetRelativeUrl, helperGetVideoUrl } from 'helpers/RouteHelper';
import WatchList, { FAVORITE_PLUS_HOVER, FAVORITE_TYPE_WATCHLIST } from 'components/Favorite';
import Description from '../Description';
import Logo from '../Logo';
import PosterCTA from '../PosterCTA';
import Video from '../Video';

const Overlay = ({
  breakpoint = null,
  descriptionLines = 4,
  favoritesManager = {},
  isHover = false,
  item = {},
  moreRoute = '',
  overlayProps = {},
  play = false,
  route = null,
  segmentPlaylistButtonAction,
  theme = {},
  video = null,
}) => {
  const [isFavorite, setFavorite] = useState(false);
  const isSimplifiedHoverEnabled = simplifiedPosterHoverEnabled();
  const autoPlayStill = getDynamicImage({
    breakpoint,
    context: POSTER_OVERLAY_IMAGE,
    url: _.get(item, 'autoPlayStill.default.url'),
  });
  const favoriteData = _.get(overlayProps, 'favoriteData', {});
  const {
    description = '',
    latestEpisodeId,
    latestFullEpisodeId,
    playerScreenUrl,
    screenUrl,
    seriesName: showName,
    seriesScreenUrl,
    isSeriesDetailAvailable,
    showCode,
  } = item;
  const isCollectionSeries = isTypeSeries(_.get(item, '@type', ''));
  const showUrl = isCollectionSeries ? screenUrl : seriesScreenUrl;
  const episodeId = latestEpisodeId || latestFullEpisodeId;
  const latestEpisodeRoute = episodeId ? helperGetVideoUrl(episodeId) : route;
  const playerRoute = isCollectionSeries ? playerScreenUrl || latestEpisodeRoute : route;
  const isVideoSeries = isSeries(item.seriesType);
  const url = moreRoute || `${helperGetRelativeUrl({ url: showUrl })}`;
  const linkText = isVideoSeries && !isCollectionSeries ? 'More Episodes' : 'More Info';
  const lines = isVideoSeries && !isCollectionSeries ? descriptionLines - 1 : descriptionLines;
  const name = isVideoSeries && !isCollectionSeries ? _.get(item, 'name') : null;
  const favorites = _.get(favoritesManager, 'favorites');
  const logo = _.get(item, 'images.logoCenter.raw', null);
  const placeholder = _.get(item, 'images.videoList.raw');
  const isOnlyWatchList = isSeries || isSeriesDetailAvailable === false;
  const isFavorited = isFavorite || _.find(favorites, ({ favoriteID }) => favoriteID === showCode);
  const hoverStateWatchlistText = `Add${isFavorited ? 'ed' : ''} to Watchlist`;
  const hoverStateToolTip = (label, toolTipClassName, isRightJustified) => (
    <div
      className={classNames([
        theme.hoverStateToolTip,
        toolTipClassName,
        isRightJustified && theme.hoverStateToolTipRight,
      ])}
    >
      <div className={theme.hoverStateToolTipLabel}>{label}</div>
      <div className={theme.hoverStateToolTipTip} />
    </div>
  );

  return (
    <>
      <div className={theme.overlayHeader}>
        {!isSimplifiedHoverEnabled && (
          <Video
            image={autoPlayStill}
            isHover={isHover}
            placeholder={placeholder}
            play={play}
            name={name}
            route={playerRoute}
            seriesName={showName}
            theme={theme}
            video={video}
            breakpoint={breakpoint}
            segmentPlaylistButtonAction={segmentPlaylistButtonAction}
          />
        )}
        <span className={theme.watchList}>
          <span className={!isHover ? theme.invisible : ''}>
            <span className={theme.watchListButtonContainer}>
              <WatchList
                type={FAVORITE_TYPE_WATCHLIST}
                stopNavigationOnClick
                manager={favoritesManager}
                iconType={FAVORITE_PLUS_HOVER}
                setFavorite={setFavorite}
                favorites={favorites}
                data={{ ...favoriteData, titleText: name, subtitleText: description }}
                segmentPlaylistButtonAction={segmentPlaylistButtonAction}
              />
            </span>
            {hoverStateToolTip(hoverStateWatchlistText, theme.watchListToolTip, isOnlyWatchList)}
          </span>
        </span>
        {!isSimplifiedHoverEnabled && (
          <div className={theme.logoContainer}>
            <Logo breakpoint={breakpoint} logo={logo} showName={showName} theme={theme} />
          </div>
        )}
      </div>
      {!isSimplifiedHoverEnabled && (
        <>
          <Description
            description={description}
            descriptionLines={lines}
            name={name}
            theme={theme}
          />
          <span className={!isHover ? theme.invisible : ''}>
            <PosterCTA
              linkText={linkText}
              segmentPlaylistButtonAction={segmentPlaylistButtonAction}
              theme={theme}
              url={url}
            />
          </span>
        </>
      )}
    </>
  );
};

Overlay.propTypes = {
  breakpoint: PropTypes.string.isRequired,
  descriptionLines: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  favoritesManager: PropTypes.shape({}).isRequired,
  isHover: PropTypes.bool.isRequired,
  item: PropTypes.shape({}).isRequired,
  moreRoute: PropTypes.string.isRequired,
  overlayProps: PropTypes.shape({}).isRequired,
  play: PropTypes.bool.isRequired,
  route: PropTypes.string.isRequired,
  segmentPlaylistButtonAction: PropTypes.func,
  theme: PropTypes.shape({}).isRequired,
  video: PropTypes.string,
};

export default Overlay;
